import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "../../utils/context/AuthContext";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import {
  SquaresFour,
  Tag,
  ShieldCheck,
  Package,
  ShoppingCart,
  UsersThree,
  Article,
  Images,
  Star,
  Megaphone,
  Storefront,
  Gear,
  SignOut,
  CaretLeft,
  CaretRight,
} from "@phosphor-icons/react";

export default function Sidebar({ isOpen, setIsOpen }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const { logout } = useAuth();
  const { checkAccess } = useAccessChecker();

  const mainNavItems = [
    { title: "Dashboard", path: "/", icon: SquaresFour, access: checkAccess("dashboard", "read") },
    { title: "Categories", path: "/categories", icon: Tag, access: checkAccess("categories", "read") },
    { title: "Brands", path: "/Brands", icon: ShieldCheck, access: checkAccess("brands", "read") },
    { title: "Products", path: "/products", icon: Package, access: checkAccess("products", "read") },
    { title: "Orders", path: "/orders", icon: ShoppingCart, access: checkAccess("orders", "read") },
    { title: "Contacts", path: "/Contacts", icon: UsersThree, access: checkAccess("contacts", "read") },
    { title: "Blogs", path: "/blogs", icon: Article, access: checkAccess("blogs", "read") },
    { title: "Media", path: "/media", icon: Images, access: checkAccess("media", "read") },
    { title: "Reviews", path: "/reviews", icon: Star, access: checkAccess("reviews", "read") },
    { title: "Pixels", path: "/Pixels", icon: Megaphone, access: checkAccess("pixels", "read") },
    { title: "Customizer", path: "/customizer", icon: Storefront, access: checkAccess("customizer", "read") },
  ];

  const toolsNavItems = [
    { title: "User Management", path: "/users", icon: UsersThree, access: checkAccess("users", "read") },
    { title: "Roles", path: "/roles", icon: ShieldCheck, access: checkAccess("roles", "read") },
    { title: "Settings", path: "/settings", icon: Gear, access: checkAccess("settings", "read") },
  ];

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 868;
      setIsMobile(mobile);
      setIsOpen(!mobile);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const handleLogOut = (e) => {
    e.preventDefault();
    logout();
    navigate("/login");
  };

  const isActive = (path) => location.pathname.toLowerCase() === path.toLowerCase();

  const NavItem = ({ item }) => {
    const active = isActive(item.path);
    const Icon = item.icon;
    return (
      <Link
        to={item.path}
        title={!isOpen ? item.title : undefined}
        className={`group relative flex items-center ${isOpen ? "gap-3 px-3.5" : "justify-center px-0"} h-11 rounded-xl text-sm font-medium transition-colors ${
          active ? "text-white" : "text-sidebar-muted hover:text-white"
        }`}
      >
        {active && (
          <motion.span
            layoutId="sidebar-active"
            transition={{ type: "spring", stiffness: 480, damping: 38 }}
            className="absolute inset-0 rounded-xl bg-white/[0.07] ring-1 ring-white/10"
          />
        )}
        {active && (
          <motion.span
            layoutId="sidebar-bar"
            transition={{ type: "spring", stiffness: 480, damping: 38 }}
            className="absolute left-0 top-1/2 h-5 w-1 top-[12px] rounded-r-full bg-accent"
          />
        )}
        <span className={`relative z-10 ${active ? "text-accent" : "text-current"}`}>
          <Icon size={21} weight={active ? "fill" : "regular"} />
        </span>
        {isOpen && <span className="relative z-10 truncate">{item.title}</span>}
      </Link>
    );
  };

  return (
    <>
      {isMobile && isOpen && (
        <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm" onClick={toggleSidebar} />
      )}

      <motion.aside
        initial={false}
        animate={{ width: isOpen ? 250 : 80 }}
        transition={{ type: "spring", stiffness: 300, damping: 32 }}
        className={`fixed left-0 top-0 tex-start z-50 flex h-full border-r border-line flex-col bg-surfaceside text-white ${
          isMobile && !isOpen ? "-translate-x-full" : "translate-x-0"
        } transition-transform duration-300`}
      >
        {/* luxurious pattern + glow overlay */}
        <div className="pointer-events-none absolute inset-0 pattern-dots opacity-70" />
        {/* <div className="pointer-events-none absolute -left-16 top-1/3 h-64 w-64 rounded-full bg-accent/10 blur-3xl" /> */}

        {/* Brand */}
        <div className={`relative flex h-[4.5rem] items-center ${isOpen ? "justify-between px-5" : "justify-center"}`}>
          <Link to="/" className="flex items-center">
            <img
              src={isOpen ? '/logotechnifro.png' : '/logo512.png'}
              alt="Technifro"
              className={`object-contain ${isOpen ? "h-14 w-auto max-w-[170px]" : "h-9 w-12"}`}
            />
          </Link>

          {!isMobile && (
            <button
              onClick={toggleSidebar}
              className={`grid h-7 w-7 place-items-center rounded-full border border-sidebar-line bg-sidebar-soft text-sidebar-muted transition hover:text-white ${
                isOpen ? "" : "absolute -right-3 top-6 z-20"
              }`}
            >
              {isOpen ? <CaretLeft size={15} weight="bold" /> : <CaretRight size={15} weight="bold" />}
            </button>
          )}
        </div>

        {/* Navigation */}
        <div className="relative flex-1  text-start overflow-y-auto no-scrollbar px-3 py-3">
          <nav className="space-y-1">
            {mainNavItems.filter((i) => i.access).map((item) => (
              <NavItem key={item.path} item={item} />
            ))}
          </nav>

          {toolsNavItems.some((i) => i.access) && (
            <div className="mt-7">
              {isOpen && (
                <div className="px-3.5 pb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-sidebar-muted/70">
                  Tools
                </div>
              )}
              <nav className="space-y-1">
                {toolsNavItems.filter((i) => i.access).map((item) => (
                  <NavItem key={item.path} item={item} />
                ))}
              </nav>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="relative border-t border-sidebar-line p-3">
          <button
            onClick={handleLogOut}
            className={`flex w-full items-center ${isOpen ? "gap-3 px-3.5" : "justify-center"} h-11 rounded-xl text-sm font-medium text-sidebar-muted transition-colors hover:bg-red-500/10 hover:text-red-400`}
          >
            <SignOut size={21} />
            {isOpen && <span>Log out</span>}
          </button>
        </div>
      </motion.aside>
    </>
  );
}
